import { actionTypes } from "./actions";
import { call, put, takeEvery, all } from 'redux-saga/effects';
import { db } from "helpers/firebase/firebaseConfig";
import { doc, updateDoc, getDoc, getDocs, collection, addDoc, increment, setDoc, query, limit } from "firebase/firestore";
import axios from 'axios';

// Collection names
const INVOICES_COLLECTION = "service_provider_sale_invoices";
const NOTIFICATIONS_COLLECTION = "serviceProvidersNotifications";
const BOOKINGS_COLLECTION = "Booking";
const SERVICE_PROVIDERS_COLLECTION = "service_providers";
const APP_USERS_COLLECTION = "app_users";
const SERVICE_PROVIDER_SERVICES_COLLECTION = "service_provider_services";
const PLATFORM_SETTINGS_COLLECTION = "platform_settings";
const SUPER_ADMIN_COLLECTION = "platform_settings";

// Node API configuration
const NODE_API = "https://node-server-eta-coral.vercel.app";
const NODE_API_KEY = process.env.REACT_APP_NODE_API_KEY;

const hasDelayKeyword = (value) => {
  const normalized = String(value || "").toLowerCase();
  return (
    normalized.includes("delay") ||
    normalized.includes("provider_delay") ||
    normalized.includes("تأخير")
  );
};

const isDelayFineEntry = (entry = {}) => {
  return (
    entry?.provider_delay_deduction === true ||
    String(entry?.fine_source_type || "").toLowerCase() === "provider_delay" ||
    String(entry?.fine_penalty_type || "").toLowerCase() === "provider_delay" ||
    String(entry?.penalty_type || "").toLowerCase() === "provider_delay" ||
    String(entry?.latest_penalty_id || "").length > 0 ||
    String(entry?.latest_delay_record_id || "").length > 0 ||
    hasDelayKeyword(entry?.fine_last_reason) ||
    hasDelayKeyword(entry?.fine_reason) ||
    (Array.isArray(entry?.deductions) &&
      entry.deductions.some((item) => hasDelayKeyword(item?.type) || hasDelayKeyword(item?.reason)))
  );
};

const getFineMeta = (entry = {}) => {
  const delayFine = isDelayFineEntry(entry);
  return {
    delayFine,
    defaultReasonAr: delayFine ? "غرامة تأخير مقدم الخدمة" : "غرامة إلغاء",
    deductionType: delayFine ? "provider_delay_fine" : "cancellation_fine",
    erpLineDescription: delayFine
      ? "Provider Delay Fine (Platform Revenue)"
      : "Cancellation Fine (Platform Revenue)",
  };
};

// Helper: Fetch platform settings from database
async function getPlatformSettings() {
  try {
    const settingsSnapshot = await getDocs(collection(db, PLATFORM_SETTINGS_COLLECTION));
    
    if (!settingsSnapshot.empty) {
      const settingsDoc = settingsSnapshot.docs[0];
      return settingsDoc.data();
    }
    
    // Fallback to default if not found
    return {
      company_name: "ﺷﺮﻛﺔ ﺍﻟﻔﻜﺮﺓ ﺍﻹﺑﺪﺍﻋﻴﻪ ﺍﻟﺘﺠﺎﺭﻳﺔ",
      vat_number: "311376152500003",
      commercial_register: "1010819082",
      address: "الرياض٫الرياض الامير عبدالعزيز بن مساعد بن جلوي، 12628",
      country: "المملكة العربية السعودية"
    };
  } catch (error) {
    console.error("Error fetching platform settings:", error);
    return {
      company_name: "ﺷﺮﻛﺔ ﺍﻟﻔﻜﺮﺓ ﺍﻹﺑﺪﺍﻋﻴﻪ ﺍﻟﺘﺠﺎﺭﻳﺔ",
      vat_number: "311376152500003",
      commercial_register: "1010819082",
      address: "الرياض٫الرياض الامير عبدالعزيز بن مساعد بن جلوي، 12628",
      country: "المملكة العربية السعودية"
    };
  }
}

// Helper: Ensure commission item exists in ERP system (SAFE - check DB first)
async function ensureERPCommissionItem() {
  try {
    const COMMISSION_ITEM_CODE = "DB-COMMISSION-001";

    // ✅ STEP 1: Check database first - if already created, don't retry
    try {
      const superAdminSnapshot = await getDocs(collection(db, SUPER_ADMIN_COLLECTION));

      if (!superAdminSnapshot.empty) {
        const superAdminDoc = superAdminSnapshot.docs[0];
        const superAdminData = superAdminDoc.data();

        // If we've already created the item in ERP, just return code
        if (superAdminData.commission_item_erp_created) {
          // console.log("✓ Commission item already created in ERP (from DB):", COMMISSION_ITEM_CODE);
          return COMMISSION_ITEM_CODE;
        }
      }
    } catch (dbErr) {

      // console.log("DB check failed, will attempt ERP creation:", dbErr.message);
    }

    // ✅ STEP 2: Only create if not already in DB
    // console.log("Creating commission item in ERP (first time only)...");

    const itemPayload = {
      item_code: COMMISSION_ITEM_CODE,
      item_name: "DBSaloni Commission",
      item_group: "Services",
      stock_uom: "Nos",
      // description: "Marketplace commission for services provided via DBSaloni",
      is_stock_item: 0,
      is_sales_item: 1,
           cancellation_fines: fineDeductions,
           net_after_fines: netAfterFines,
           fine_reason: documentId.fine_last_reason || "غرامة إلغاء",
           deductions:
             fineDeductions > 0
               ? [
                 {
                   type: "cancellation_fine",
                   amount: fineDeductions,
                   reason: documentId.fine_last_reason || "غرامة إلغاء",
                   source: documentId.latest_cancellation_id || null,
                 },
               ]
               : [],
      income_account: "4210 - Commission Income - DS",
      item_tax_template: "Standard Vat 15% - DS",
      tax_category: "المملكة العربية السعودية VAT",
    };

    const createRes = await fetch(`${NODE_API}/item`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": NODE_API_KEY,
      },
      body: JSON.stringify(itemPayload),
    });

    const createData = await createRes.json();

    if (!createRes.ok || createData?.error || createData?.exc_type) {
      const message = createData?.message || createData?.error || "";
      if (typeof message === 'string' && (message.includes('DuplicateEntry') || message.includes('already exists'))) {
        // console.log("✓ Item already exists in ERP (soft-fail handled):", COMMISSION_ITEM_CODE);
        return COMMISSION_ITEM_CODE;
      }
      console.error("⚠ ERP item creation warning:", message);
      return COMMISSION_ITEM_CODE;
    }

    // console.log("✓ Commission item created successfully in ERP:", COMMISSION_ITEM_CODE);
    return COMMISSION_ITEM_CODE;

  } catch (error) {
    console.error("Error ensuring ERP commission item:", error);
    return "DB-COMMISSION-001";
  }
}

// Helper: Get or generate commission item code from super admin collection
async function getOrGenerateCommissionItemCode() {
  try {
    const superAdminSnapshot = await getDocs(collection(db, SUPER_ADMIN_COLLECTION));

    if (superAdminSnapshot.empty) {
      // No super admin found, create one with initial item code
      // Also ensure the item exists in ERP
      const commissionItemCode = await ensureERPCommissionItem();

      const newSuperAdmin = {
        commission_item_code: commissionItemCode,
        commission_item_erp_created: true,
        commission_item_created_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      };

      await addDoc(collection(db, SUPER_ADMIN_COLLECTION), newSuperAdmin);

      return commissionItemCode;
    }

    // Get first super admin document
    const superAdminDoc = superAdminSnapshot.docs[0];
    const superAdminData = superAdminDoc.data();

    // Check if commission_item_code exists
    if (superAdminData.commission_item_code) {
      // Verify item exists in ERP on first use
      if (!superAdminData.commission_item_erp_created) {
        await ensureERPCommissionItem();
        await updateDoc(doc(db, SUPER_ADMIN_COLLECTION, superAdminDoc.id), {
          commission_item_erp_created: true,
          commission_item_created_at: new Date().toISOString()
        });
      }
      return superAdminData.commission_item_code;
    }

    // If not, generate and create the item
    const newItemCode = await ensureERPCommissionItem();
    await updateDoc(doc(db, SUPER_ADMIN_COLLECTION, superAdminDoc.id), {
      commission_item_code: newItemCode,
      commission_item_erp_created: true,
      commission_item_created_at: new Date().toISOString()
    });

    return newItemCode;

  } catch (error) {
    console.error("Error getting or generating commission item code:", error);
    // Fallback to default if something goes wrong
    return "DB-COMMISSION-001";
  }
}

// Helper: Create or get ERP customer
async function ensureERPCustomer(provider) {
  try {
    // Check if provider already has ERP account
    if (provider.erp_account?.customer_id) {
      // console.log("Provider already has ERP account:", provider.erp_account.customer_id);
      return provider.erp_account.customer_id;
    }

    const customerName = provider.business_name || provider.salon_name || "Provider Customer";

    // First, try to fetch existing customer
    try {
      const searchRes = await fetch(
        `https://node-server-eta-coral.vercel.app/customer?customer_name=${encodeURIComponent(customerName)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_NODE_API_KEY
          }
        }
      );

      const searchJson = await searchRes.json();

      if (searchRes.ok && searchJson?.data?.data && searchJson.data.data.length > 0) {
        const existingCustomer = searchJson.data.data[0];
        // console.log("Found existing customer:", existingCustomer.name);

        // Save ERP account info to provider in Firebase
        await updateDoc(doc(db, "service_providers", provider.id), {
          erp_account: {
            customer_name: existingCustomer.name,
            customer_id: existingCustomer.name,
            created_at: new Date().toISOString(),
            is_active: true
          }
        });

        return existingCustomer.name;
      }
    } catch (searchErr) {
      // console.log("Search failed, will try to create:", searchErr.message);
    }

    // If customer doesn't exist, create new one
    const customerPayload = {
      customer_name: customerName,
      customer_name_in_arabic: provider.salon_name_ar || provider.salon_name || "Provider",
      customer_type: "Individual",
      contact_email: provider.email || "no-email@dbsaloni.com",
      mobile_no: provider.mobile || "0000000000",
      custom_b2c: 1,
      custom_buyer_id_type: "OTH",
    };

    // console.log("Creating ERP customer with payload:", customerPayload);

    const response = await fetch(
      'https://node-server-eta-coral.vercel.app/customer',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_NODE_API_KEY
        },
        body: JSON.stringify(customerPayload),
      }
    );

    const json = await response.json();

    if (!response.ok || json?.error || json?.exc_type) {
      const message = json?.message || json?.error || "Failed to create ERP customer";
      // console.error("ERP customer creation failed:", message);
      throw new Error(typeof message === "string" ? message : JSON.stringify(message));
    }

    const erpCustomerId = json?.data?.data?.name || json?.data?.name || json?.name;

    if (!erpCustomerId) {
      // console.error("No customer ID in response:", json);
      throw new Error("Customer ID not found in ERP response");
    }

    // console.log("ERP customer created:", erpCustomerId);

    // Save ERP account info to provider in Firebase
    await updateDoc(doc(db, "service_providers", provider.id), {
      erp_account: {
        customer_name: customerPayload.customer_name,
        customer_id: erpCustomerId,
        created_at: new Date().toISOString(),
        is_active: true
      }
    });

    return erpCustomerId;
  } catch (error) {
    console.error("Error ensuring ERP customer:", error);
    throw error;
  }
}

async function createCommissionERPInvoice(
  documentId,
  appCommission,
  appCommissionVAT,
  serviceProviderCommission,
  serviceProviderCommissionVAT,
  tamaraCommission,
  tamaraCommissionVAT,
  fineDeductions
) {
  try {
    const fineMeta = getFineMeta(documentId);

    // Calculate total amounts for all commission types
    const totalCommission = appCommission + serviceProviderCommission + tamaraCommission;
    const totalCommissionVAT = appCommissionVAT + serviceProviderCommissionVAT + tamaraCommissionVAT;
    const totalAmount = totalCommission + totalCommissionVAT + (Number(fineDeductions) || 0);

    const today = new Date().toISOString().split("T")[0];

    // Ensure provider has ERP customer account (service provider is the customer)
    let erpCustomerId = documentId.serviceProviders?.erp_account?.customer_id;
    if (!erpCustomerId) {
      erpCustomerId = await ensureERPCustomer(documentId.serviceProviders);
    }

    // Get or generate commission item code from super admin collection
    const commissionItemCode = await getOrGenerateCommissionItemCode();

     const invoiceRef = doc(db, INVOICES_COLLECTION, documentId.id);

         // Update invoice with payment_status, commission_erp_id, and settlement_id
    await updateDoc(invoiceRef, {
      commission_item_code: commissionItemCode
    });


    // Build line items array with both commission types
    const items = [];

    // Line 1: App Commission
    if (appCommission > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: appCommission,
        description: "DB Saloni App Commission (Platform Revenue)",
        item_tax_template: "Standard Vat 15% - DS"
      });
    }

    // Line 2: Service Provider Commission
    if (serviceProviderCommission > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: serviceProviderCommission,
        description: "Service Provider Commission",
        item_tax_template: "Standard Vat 15% - DS"
      });
    }

    // Line 3: Tamara Commission
    if (tamaraCommission > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: tamaraCommission,
        description: "Tamara Commission",
        item_tax_template: "Standard Vat 15% - DS"
      });
    }

    // Line 4: Fine deduction (delay/cancellation) added to platform revenue
    if ((Number(fineDeductions) || 0) > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: Number(fineDeductions) || 0,
        description: fineMeta.erpLineDescription,
        // Fines are added without VAT component
      });
    }

    // ERP Payload for dual-commission invoice (Saudi compliant)
    const payload = {
      company:"DB Saloni",
      customer: erpCustomerId,
      posting_date: today,
      due_date: today,
      mode_of_payment: "Cash",
      debit_to: "1310 - Debtors - DS",
      cost_center: "Main - DS",
      is_pos: 1,

      payments: [
        {
          mode_of_payment: "Cash",
          amount: totalAmount,
          base_amount: totalAmount,
          account: "1110 - Cash - DS"
        }
      ],

      items: items,

      paid_amount: totalAmount,
      docstatus: 1
    };

    const response = await axios.post(
      "https://node-server-eta-coral.vercel.app/invoice",
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_NODE_API_KEY
        }
      }
    );

    const erpData = response.data?.data || response.data;
// console.log("Commission ERP invoice created:", erpData);
    if (!erpData || !erpData.name) {
      throw new Error("ERP commission invoice creation failed: No invoice ID returned");
    }

    // Store commission invoice data in Firebase with dual-commission breakdown
    const firebaseData = {
      invoice_id: documentId.id,
      erp_commission_invoice_id: erpData.name,
      erp_commission_invoice_data: erpData,

      service_provider_id: documentId.serviceProviders?.id || null,
      service_provider_name: documentId.serviceProviders?.salon_name || null,
      service_name: documentId.serviceProviderServices?.name || null,

      commission_item_code: commissionItemCode,
      
      // Dual commission breakdown
      app_commission: appCommission,
      app_commission_vat: appCommissionVAT,
      service_provider_commission: serviceProviderCommission,
      service_provider_commission_vat: serviceProviderCommissionVAT,
      tamara_commission: tamaraCommission,
      tamara_commission_vat: tamaraCommissionVAT,
      
      total_commission: totalCommission,
      total_commission_vat: totalCommissionVAT,
      total_platform_revenue: totalAmount,
      fine_deductions_total: Number(fineDeductions) || 0,

      status: "completed",
      created_at: new Date().toISOString(),
      posting_date: today,
      customer: erpCustomerId
    };

    await addDoc(collection(db, "commission_erp_invoices"), firebaseData);

    return erpData;

  } catch (error) {
    console.error("Error creating commission ERP invoice:", error.response?.data || error.message);
    throw error;
  }
}

// NEW: Batched Commission ERP Invoice (for multiple service invoices)
async function createBatchedCommissionERPInvoice(invoices, serviceProvider, startDate, endDate) {
  try {
    const VAT_RATE = 0.15;
    
    // Aggregate all commissions and fines from invoices
    let totalAppCommission = 0;
    let totalServiceProviderCommission = 0;
    let totalTamaraCommission = 0;
    let totalFines = 0;
    
    const invoiceDetails = [];

    invoices.forEach(invoice => {
      const fineDeductions = Number(invoice.fine_deductions_total || 0);
      const grossAmount = Number(
        invoice.total_after_discount_before_fine ||
        invoice.total_after_discount ||
        0
      );
      
      const serviceVAT = grossAmount * (VAT_RATE / (1 + VAT_RATE));
      const serviceNet = grossAmount - serviceVAT;
      
      const appCommissionRate = Number(invoice.app_commission_rate || 0);
      const serviceProviderCommissionRate = Number(invoice.service_provider_commission_rate || 0);
      const paymentMethod = String(
        invoice?.booking?.paymentMethod || invoice?.booking?.payment_method || invoice?.payment_method || ""
      ).toLowerCase();
      const tamaraCommissionRate = paymentMethod === "tamara" ? Number(invoice.tamara_commission || 0) : 0;
      
      const appCommission = serviceNet * (appCommissionRate / 100);
      const serviceProviderCommission = serviceNet * (serviceProviderCommissionRate / 100);
      const tamaraCommission = serviceNet * (tamaraCommissionRate / 100);
      
      totalAppCommission += appCommission;
      totalServiceProviderCommission += serviceProviderCommission;
      totalTamaraCommission += tamaraCommission;
      totalFines += fineDeductions;

      invoiceDetails.push({
        invoice_id: invoice.id,
        service_name: invoice.serviceProviderServices?.name || "Unknown Service",
        customer_name: invoice.app_user_id?.username || "Unknown Customer",
        gross_amount: grossAmount,
        app_commission: appCommission,
        service_provider_commission: serviceProviderCommission,
        tamara_commission_rate: tamaraCommissionRate,
        tamara_commission: tamaraCommission,
        fine_deductions: fineDeductions,
        date: invoice.created_at
      });
    });

    const totalCommission = totalAppCommission + totalServiceProviderCommission + totalTamaraCommission;
    const totalCommissionVAT = totalCommission * VAT_RATE;
    const totalAmount = totalCommission + totalCommissionVAT + totalFines;

    const today = new Date().toISOString().split("T")[0];

    // Ensure provider has ERP customer account
    let erpCustomerId = serviceProvider?.erp_account?.customer_id;
    if (!erpCustomerId) {
      erpCustomerId = await ensureERPCustomer(serviceProvider);
    }

    // Get commission item code
    const commissionItemCode = await getOrGenerateCommissionItemCode();

    // Build batched line items
    const items = [];

    // Line 1: Total App Commission for period
    if (totalAppCommission > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: totalAppCommission,
        description: `DB Saloni App Commission (${startDate} to ${endDate}) - ${invoices.length} services`,
        item_tax_template: "Standard Vat 15% - DS"
      });
    }

    // Line 2: Total Service Provider Commission for period
    if (totalServiceProviderCommission > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: totalServiceProviderCommission,
        description: `Service Provider Commission (${startDate} to ${endDate}) - ${invoices.length} services`,
        item_tax_template: "Standard Vat 15% - DS"
      });
    }

    // Line 3: Total Tamara Commission for period
    if (totalTamaraCommission > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: totalTamaraCommission,
        description: `Tamara Commission (${startDate} to ${endDate}) - ${invoices.length} services`,
        item_tax_template: "Standard Vat 15% - DS"
      });
    }

    const hasDelayFinesInBatch = invoices.some((invoice) => isDelayFineEntry(invoice));

    // Line 4: Total fines for period
    if (totalFines > 0) {
      items.push({
        item_code: commissionItemCode,
        qty: 1,
        rate: totalFines,
        description: hasDelayFinesInBatch
          ? `Service Delay/Cancellation Fines (${startDate} to ${endDate})`
          : `Cancellation Fines (${startDate} to ${endDate})`,
      });
    }

    // ERP Payload for batched commission invoice
    const payload = {
      company: "DB Saloni",
      customer: erpCustomerId,
      posting_date: today,
      due_date: today,
      mode_of_payment: "Cash",
      debit_to: "1310 - Debtors - DS",
      cost_center: "Main - DS",
      is_pos: 1,

      payments: [
        {
          mode_of_payment: "Cash",
          amount: totalAmount,
          base_amount: totalAmount,
          account: "1110 - Cash - DS"
        }
      ],

      items: items,

      paid_amount: totalAmount,
      docstatus: 1
    };

    const response = await axios.post(
      "https://node-server-eta-coral.vercel.app/invoice",
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_NODE_API_KEY
        }
      }
    );

    const erpData = response.data?.data || response.data;

    if (!erpData || !erpData.name) {
      throw new Error("Batched ERP commission invoice creation failed: No invoice ID returned");
    }

    // Store batched commission invoice data in Firebase
    const firebaseData = {
      batch_settlement_id: `BATCH-${Date.now()}`,
      erp_commission_invoice_id: erpData.name,
      erp_commission_invoice_data: erpData,

      service_provider_id: serviceProvider?.id || null,
      service_provider_name: serviceProvider?.salon_name || null,
      
      period_start: startDate,
      period_end: endDate,
      invoice_count: invoices.length,
      
      commission_item_code: commissionItemCode,
      
      // Aggregated commission breakdown
      total_app_commission: totalAppCommission,
      app_commission_vat: totalAppCommission * VAT_RATE,
      total_service_provider_commission: totalServiceProviderCommission,
      service_provider_commission_vat: totalServiceProviderCommission * VAT_RATE,
      total_tamara_commission: totalTamaraCommission,
      tamara_commission_vat: totalTamaraCommission * VAT_RATE,
      
      total_commission: totalCommission,
      total_commission_vat: totalCommissionVAT,
      total_fines: totalFines,
      total_platform_revenue: totalAmount,

      invoice_details: invoiceDetails,

      status: "completed",
      created_at: new Date().toISOString(),
      posting_date: today,
      customer: erpCustomerId,
      is_batched: true
    };

    const batchRef = await addDoc(collection(db, "commission_erp_invoices"), firebaseData);

    return {
      erpData,
      batchSettlementId: firebaseData.batch_settlement_id,
      batchDocId: batchRef.id
    };

  } catch (error) {
    console.error("Error creating batched commission ERP invoice:", error.response?.data || error.message);
    throw error;
  }
}

// Function to store settlement data in Firestore (customer settlements only)
async function createSettlementStatement(documentId, settlementData) {
  try {
    const provider = documentId.serviceProviders;
    const cancelledBy = settlementData.cancelled_by || documentId.cancelled_by || "provider";
    const isRefundSettlement =
      settlementData.is_refund_settlement === true ||
      documentId?.is_refund === true ||
      documentId?.needs_refund === true ||
      documentId?.refund_status === "pending" ||
      cancelledBy === "app_user" ||
      cancelledBy === "app__user";
    const resolvedFinalPayout =
      settlementData.final_payout !== undefined
        ? Number(settlementData.final_payout || 0)
        : Number((settlementData.net_amount_with_vat || 0) - (settlementData.cancellation_fines || 0));
    const revenueCountedAmount = isRefundSettlement ? 0 : resolvedFinalPayout;
    const refundAmount = isRefundSettlement ? resolvedFinalPayout : 0;

    // Fetch platform settings from database
    const platformSettings = await getPlatformSettings();


    const fullSettlement = {
      // 🧾 Identity
      platform: platformSettings,

      service_provider: {
        id: provider?.id,
        name: provider?.salon_name || provider?.full_name,
        commercial_register: provider?.commercial_register || null,
        vat_number: provider?.tax_number || null,
        address: provider?.entity_unified_no || null,
        mobile: provider?.mobile || null,
        entity_unified_no: provider?.entity_unified_no || null,
      },

      customer: {
        name: documentId.app_user_id?.username || "Unknown",
        mobile: documentId.app_user_id?.mobile || null
      },

      // 📅 Period
      period: {
        from: documentId.booking?.created_at || documentId.created_at,
        to: new Date().toISOString()
      },

      // 💰 Financials (After commission deduction)
      amounts: {
        original_service_amount: settlementData.original_service_amount || 0, // Original full amount
        platform_commission: settlementData.platform_commission || 0, // Commission deducted
        app_commission: settlementData.app_commission || 0,
        service_provider_commission: settlementData.service_provider_commission || 0,
        tamara_commission_rate: settlementData.tamara_commission_rate || 0,
        tamara_commission: settlementData.tamara_commission || 0,
        app_commission_vat: settlementData.app_commission_vat || 0,
        service_provider_commission_vat: settlementData.service_provider_commission_vat || 0,
        tamara_commission_vat: settlementData.tamara_commission_vat || 0,
        amount_after_commission: settlementData.amount_after_commission || 0, // After commission, before VAT
        service_vat: settlementData.service_vat || 0, // 15% VAT on net amount
        net_amount_with_vat: settlementData.net_amount_with_vat || 0, // Final amount with VAT
        cancellation_fines: settlementData.cancellation_fines || 0,
        final_payout: resolvedFinalPayout,
        revenue_counted_amount: revenueCountedAmount,
        refund_amount: refundAmount,
        // Legacy fields for backward compatibility
        gross_service_amount: settlementData.gross_amount || 0,
        service_net: settlementData.service_net || 0,
        net_after_fines:
          settlementData.net_after_fines !== undefined
            ? settlementData.net_after_fines
            : (settlementData.gross_amount || 0) - (settlementData.cancellation_fines || 0),
      },

      deductions: settlementData.deductions || [],
      fine_reason: settlementData.fine_reason || null,
      
      // ✅ NEW: Track who was charged for fine
      cancelled_by: cancelledBy,
      fine_charged_to:
        cancelledBy === "app_user" || cancelledBy === "app__user"
          ? "app_user"
          : "provider",
      is_refund_settlement: isRefundSettlement,
      settlement_type: isRefundSettlement ? "refund" : "standard",

      // 🔗 References
      booking_id: documentId.booking?.id,
      booking: documentId.booking || null,
      invoice_id: documentId.id,
      erp_commission_invoice_id: documentId.commission_erp_id || null,
      batch_settlement_id: settlementData.batch_settlement_id || documentId.batch_settlement_id || null,
      batch_erp_invoice_id: settlementData.batch_erp_invoice_id || documentId.batch_erp_invoice_id || null,
      period_start: settlementData.period_start || null,
      period_end: settlementData.period_end || null,
      is_batched: settlementData.is_batched || false,
      invoice_count: settlementData.invoice_count || 1,

      status: "generated",
      created_at: new Date().toISOString()
    };

    const ref = await addDoc(collection(db, "customer_settlements"), fullSettlement);

    return { settlement_id: ref.id };

  } catch (error) {
    console.error("Error creating settlement:", error);
    throw error;
  }
}


async function updateStatus(documentId) {
  try {
    const VAT_RATE = 0.15;
    const invoiceRef = doc(db, INVOICES_COLLECTION, documentId.id);

    const normalize = (value) => String(value || "").toLowerCase();
    const paidValues = ["paid", "completed", "success", "succeeded"];
    let resolvedBooking =
      documentId?.booking && typeof documentId.booking === "object" ? documentId.booking : null;

    if (!resolvedBooking && typeof documentId?.booking === "string") {
      const bookingSnap = await getDoc(doc(db, BOOKINGS_COLLECTION, documentId.booking));
      if (bookingSnap.exists()) {
        resolvedBooking = { id: bookingSnap.id, ...bookingSnap.data() };
      }
    }

    const bookingStatus = normalize(resolvedBooking?.booking_status);
    const paymentStatus = normalize(resolvedBooking?.paymentStatus || resolvedBooking?.payment_status);
    const isBookingCompletedAndPaid =
      bookingStatus === "completed" && paidValues.includes(paymentStatus);
    const isRefundMarked = documentId?.needs_refund === true;
    const cancelledBy = documentId.cancelled_by || "provider";
    const isCancelledByAppUser = cancelledBy === "app_user" || cancelledBy === "app__user";
    const fineChargedTo = documentId?.fine_charged_to || (isCancelledByAppUser ? "app_user" : "provider");
    const isFineDeducted =
      documentId?.fine_deducted === true ||
      String(documentId?.fine_deducted || "").toLowerCase() === "true";

    if (isCancelledByAppUser && !isFineDeducted) {
      throw new Error("Cannot create ERP invoice: cancellation fine must be deducted first.");
    }

    if (!isRefundMarked && !isBookingCompletedAndPaid) {
      throw new Error("Cannot create ERP invoice: booking must be completed and paid by user.");
    }

    // Get commission rates from invoiceData
    const appCommissionRate = documentId.app_commission_rate || 0; // App commission is fixed at 0%
    const serviceProviderCommissionRate = documentId.service_provider_commission_rate || 0;
    const paymentMethod = normalize(
      resolvedBooking?.paymentMethod || resolvedBooking?.payment_method || documentId?.payment_method
    );
    const tamaraCommissionRate = paymentMethod === "tamara" ? Number(documentId.tamara_commission || 0) : 0;

    // Calculate settlement values (respect fines applied to invoice)
    const fineDeductions =
      documentId.fine_deductions_total || documentId.total_deductions || 0;
    const fineMeta = getFineMeta(documentId);
    const grossSaleAmount =
      documentId.total_after_discount_before_fine ||
      documentId.total_after_discount ||
      documentId.amount ||
      0;
    const netAfterFines =
      documentId.net_after_fines !== undefined
        ? documentId.net_after_fines
        : Math.max(grossSaleAmount - fineDeductions, 0);

    const serviceVAT = grossSaleAmount * (VAT_RATE / (1 + VAT_RATE)); // 15/115
    const serviceNet = grossSaleAmount - serviceVAT;
    
    // Calculate both commissions on service net
    const appCommission = serviceNet * (appCommissionRate / 100);
    const appCommissionVAT = appCommission * VAT_RATE;

    const serviceProviderCommission = serviceNet * (serviceProviderCommissionRate / 100);
    const serviceProviderCommissionVAT = serviceProviderCommission * VAT_RATE;

    const tamaraCommission = serviceNet * (tamaraCommissionRate / 100);
    const tamaraCommissionVAT = tamaraCommission * VAT_RATE;
    
    const totalCommission = appCommission + serviceProviderCommission + tamaraCommission;
    const totalCommissionVAT = appCommissionVAT + serviceProviderCommissionVAT + tamaraCommissionVAT;
    const totalPlatformRevenue = totalCommission + totalCommissionVAT + fineDeductions;
    const isRefundSettlement =
      documentId?.is_refund === true ||
      documentId?.needs_refund === true ||
      documentId?.refund_status === "pending" ||
      isCancelledByAppUser;
    const refundAppliedFine = fineChargedTo === "app_user" ? fineDeductions : 0;
    const revenueForPlatform = isRefundSettlement
      ? (fineChargedTo === "provider" ? fineDeductions : 0)
      : totalPlatformRevenue;
    
    // Calculate what service provider actually receives
    // ✅ For refund/cancellation settlements: DO NOT deduct commission in settlement, deduct fines only.
    const amountAfterCommission = isRefundSettlement
      ? serviceNet
      : serviceNet - totalCommission; // Net amount after commission deduction (before VAT)
    const vatOnNetAmount = amountAfterCommission * VAT_RATE; // 15% VAT on the net amount
    const netAmountWithVAT = isRefundSettlement
      ? grossSaleAmount
      : amountAfterCommission + vatOnNetAmount; // Net amount with VAT
    const finalPayout = Math.max(netAmountWithVAT - refundAppliedFine, 0); // Refund fine only applies when cancelled by app_user
    
    const netPayoutToSalon = finalPayout;

    // Create commission ERP invoice with dual-commission structure
    const erpCommissionInvoice = await createCommissionERPInvoice(
      documentId,
      appCommission,
      appCommissionVAT,
      serviceProviderCommission,
      serviceProviderCommissionVAT,
      tamaraCommission,
      tamaraCommissionVAT,
      fineDeductions
    );


    // Prepare settlement data (showing service provider's actual payout after commission)
    const settlementData = {
      order_id: documentId.booking?.id || null,
      customer_name: documentId.app_user_id?.username || "Unknown User",
      // New commission-aware fields
      original_service_amount: grossSaleAmount, // Original full amount
      platform_commission: isRefundSettlement ? 0 : (totalCommission + totalCommissionVAT), // No commission on cancellation settlements
      app_commission: isRefundSettlement ? 0 : appCommission,
      service_provider_commission: isRefundSettlement ? 0 : serviceProviderCommission,
      tamara_commission_rate: tamaraCommissionRate,
      tamara_commission: isRefundSettlement ? 0 : tamaraCommission,
      app_commission_vat: isRefundSettlement ? 0 : appCommissionVAT,
      service_provider_commission_vat: isRefundSettlement ? 0 : serviceProviderCommissionVAT,
      tamara_commission_vat: isRefundSettlement ? 0 : tamaraCommissionVAT,
      amount_after_commission: amountAfterCommission, // After commission, before VAT
      service_vat: vatOnNetAmount, // VAT on net amount
      net_amount_with_vat: netAmountWithVAT, // Net with VAT
      cancellation_fines: refundAppliedFine,
      provider_fine_amount: fineChargedTo === "provider" ? fineDeductions : 0,
      final_payout: finalPayout, // Final amount after everything
      // Legacy fields for backward compatibility
      gross_amount: grossSaleAmount,
      service_vat: serviceVAT,
      service_net: serviceNet,
      net_after_fines: netAfterFines,
      payment_reference: documentId.id,
      date: new Date().toISOString(),
      service_name: documentId.serviceProviderServices?.name || null,
      service_provider_name: documentId.serviceProviders?.salon_name || null,
      fine_reason: documentId.fine_last_reason || fineMeta.defaultReasonAr,
      cancelled_by: cancelledBy,
      fine_charged_to: fineChargedTo,
      is_refund_settlement: isRefundSettlement,
      deductions:
        refundAppliedFine > 0
          ? [
              {
                type: fineMeta.deductionType,
                amount: refundAppliedFine,
                reason: documentId.fine_last_reason || fineMeta.defaultReasonAr,
                source:
                  documentId.latest_delay_record_id || documentId.latest_cancellation_id || null,
              },
            ]
          : [],
    };

    // Create settlement statement and PDF
    const settlement = await createSettlementStatement(documentId, settlementData);

    // Update invoice with payment_status, commission_erp_id, and settlement_id
    await updateDoc(invoiceRef, {
      payment_status: 'paid',
      commission_erp_id: erpCommissionInvoice?.name || null,
      settlement_id: settlement.settlement_id,
      constant_commission: serviceProviderCommissionRate,
      // Add commission breakdown fields for tracking
      app_commission: appCommission,
      app_commission_vat: appCommissionVAT,
      service_provider_commission: serviceProviderCommission,
      service_provider_commission_vat: serviceProviderCommissionVAT,
      tamara_commission_rate: tamaraCommissionRate,
      tamara_commission: tamaraCommission,
      tamara_commission_vat: tamaraCommissionVAT,
      fine_deductions_total: fineDeductions,
      net_after_fines: netAfterFines,
      total_platform_revenue: revenueForPlatform,
      fine_charged_to: fineChargedTo,
      is_refund_settlement: isRefundSettlement,
      settlement_type: isRefundSettlement ? "refund" : "standard"
    });

    const invoiceSnap = await getDoc(invoiceRef);
    if (!invoiceSnap.exists()) throw new Error("Invoice not found");

    const invoiceData = invoiceSnap.data();

    const [bookingSnap, providerSnap] = await Promise.all([
      getDoc(doc(db, BOOKINGS_COLLECTION, invoiceData.booking)),
      getDoc(doc(db, SERVICE_PROVIDERS_COLLECTION, invoiceData.serviceProviders))
    ]);

    const bookingData = bookingSnap.exists() ? bookingSnap.data() : null;
    const providerData = providerSnap.exists() ? providerSnap.data() : null;
    const platformSettings = await getPlatformSettings();

    // Update transactionData to reflect new dual-commission model
    const transactionData = {
      service_name: documentId?.serviceProviderServices?.name || "Unknown Service",
      service_provider_name: documentId?.serviceProviders?.full_name || "Unknown Provider",
      service_provider_id: documentId?.serviceProviders?.id || null, 
      service_provider_mobile: documentId?.serviceProviders?.mobile || "Unknown Mobile",
      service_provider_vat_number: providerData?.tax_number || "Unknown VAT",
      service_provider_commercial_register: providerData?.commercial_register || "Unknown Register",
      service_provider_address: providerData?.address || "Unknown Address",
      user_name: documentId?.app_user_id?.username || "Unknown User",
      amount_paid: documentId.total || 0,
      erp_invoice_id: erpCommissionInvoice?.name || null, // Use generated ERP invoice ID
      total_after_discount: documentId.total_after_discount || 0,
      gross_sale_amount: grossSaleAmount,  
      fine_deductions_total: fineDeductions,
      net_after_fines: netAfterFines,
      commission_rates: serviceProviderCommissionRate,
      payment_method: documentId?.booking?.paymentMethod || "Unknown Method",
      transaction_date: new Date().toISOString(),
      app_user_mobile: documentId?.app_user_id?.mobile || "Unknown Mobile",
      booking_id: documentId?.booking.id || null,
      invoice_id: documentId?.id,
      service_id: documentId?.serviceProviderServices?.id || null,
      status: "paid",
      created_at: new Date().toISOString(),
      // Dual-commission breakdown for tracking
      app_commission: isRefundSettlement ? 0 : appCommission,
      app_commission_vat: isRefundSettlement ? 0 : appCommissionVAT,
      service_provider_commission: isRefundSettlement ? 0 : serviceProviderCommission,
      service_provider_commission_vat: isRefundSettlement ? 0 : serviceProviderCommissionVAT,
      tamara_commission_rate: tamaraCommissionRate,
      tamara_commission: isRefundSettlement ? 0 : tamaraCommission,
      tamara_commission_vat: isRefundSettlement ? 0 : tamaraCommissionVAT,
      total_commission: isRefundSettlement ? 0 : totalCommission,
      total_commission_vat: isRefundSettlement ? 0 : totalCommissionVAT,
      fine_deductions_total: fineDeductions,
      cancellation_fines: settlementData.cancellation_fines,
      provider_fine_amount: settlementData.provider_fine_amount,
      fine_reason: settlementData.fine_reason,
      deductions: settlementData.deductions,
      fine_type: fineMeta.delayFine ? "provider_delay" : "cancellation",
      fine_charged_to: fineChargedTo,
      total_platform_revenue: revenueForPlatform,
      net_after_fines: netAfterFines,
      original_service_amount: settlementData.original_service_amount,
      platform_commission: settlementData.platform_commission,
      amount_after_commission: settlementData.amount_after_commission,
      net_amount_with_vat: settlementData.net_amount_with_vat,
      final_payout: settlementData.final_payout,
      net_payout: netPayoutToSalon,
      is_refund_settlement: isRefundSettlement,
      settlement_type: isRefundSettlement ? "refund" : "standard",
      item_code: invoiceData.item_code || null,
      platform: platformSettings,
    };

    // Increment platform's revenue (total commission before VAT)
    await addDoc(collection(db, "providers_transactions_history"), transactionData);

    if (!isRefundSettlement) {
      await setDoc(
        doc(db, "platform_revenue", "total"),
        {
          amount: increment(totalCommission + fineDeductions),
          last_updated: new Date().toISOString(),
          last_fine_component: fineDeductions,
        },
        { merge: true }
      );
    } else if (fineChargedTo === "provider" && fineDeductions > 0) {
      await setDoc(
        doc(db, "platform_revenue", "total"),
        {
          amount: increment(fineDeductions),
          last_updated: new Date().toISOString(),
          last_fine_component: fineDeductions,
        },
        { merge: true }
      );
    }

    return {
      ...documentId,
      erpCommissionInvoice,
      commission_erp_id: erpCommissionInvoice?.name,
      settlement_id: settlement.settlement_id,
      commission_rates: serviceProviderCommissionRate,
      app_commission: appCommission,
      service_provider_commission: serviceProviderCommission,
      tamara_commission: tamaraCommission,
      total_platform_revenue: totalPlatformRevenue,
    
    };
  } catch (error) {
    console.error("Error updating status:", error);
    throw error;
  }
}

function* confirmPaymentSaga(action) {
  try {
    const { documentId } = action;
    const response = yield call(updateStatus, documentId);

    if (response) {
      // Prepare notification data (update content to reflect settlement)
      const notificationData = {
        title: `تم تأكيد تسوية مستحقاتك`,
        content: `نود إعلامك بأنه تم إصدار بيان تسوية بنجاح من قِبل الإدارة مقابل الخدمة التي قدمتها: 
        ${response.serviceProviderServices?.name || 'خدمة غير معروفة'}.
      
      اسم العميل: ${response?.app_user_id?.username || 'عميل غير معروف'}.
      
      يمكنك تنزيل بيان التسوية من لوحة التحكم الخاصة بك.
      
      نشكرك على احترافيتك وتعاونك الدائم معنا.`,
        notification_type: "SETTLEMENT",
        orderId: response.booking?.id || '',
        seen: false,
        serviceProviders: response.serviceProviders?.id || '',
        created_at: new Date().toISOString(),
      };

      // Save only one document in Firestore
      yield call(saveNotificationToFirestore, notificationData);

      // Get notification tokens
      const notificationTokens = Array.isArray(response.serviceProviders?.notification_token)
        ? response.serviceProviders.notification_token.filter(t => t)
        : [];

      // Send push notifications for each token
      yield all(
        notificationTokens.map(token =>
          call(handleNotificationWithRetry, notificationData, token)
        )
      );
    }

    const transactions = yield call(fetchInvoices);
 
    yield put({ type: actionTypes.SET_TRANSACTIONS, data: transactions });

  } catch (error) {
    console.error("Error confirming payment:", error);
    yield put({ type: actionTypes.PAYMENT_CONFIRMATION_ERROR, error });
  }
}

// NEW: Batch Settlement Saga
function* generateBatchSettlementSaga(action) {
  try {
    const { serviceProviderId, startDate, endDate, invoices } = action.payload;

    if (!invoices || invoices.length === 0) {
      throw new Error("No invoices selected for batch settlement");
    }

    const isRefundMarked = (invoice) => {
      const refundStatuses = ["pending", "approved", "completed", "refunded"];
      return (
        invoice?.is_refund_settlement === true ||
        invoice?.settlement_type === "refund" ||
        invoice?.needs_refund === true ||
        refundStatuses.includes(invoice?.refund_status) ||
        Boolean(invoice?.refund_request_id)
      );
    };

    const normalize = (value) => String(value || "").toLowerCase();
    const isBookingCompletedAndPaid = (booking) => {
      const bookingStatus = normalize(booking?.booking_status);
      const paymentStatus = normalize(booking?.paymentStatus || booking?.payment_status);
      const paidValues = ["paid", "completed", "success", "succeeded"];
      return bookingStatus === "completed" && paidValues.includes(paymentStatus);
    };

    const invoicesWithBooking = yield all(
      invoices.map(function* (invoice) {
        let resolvedBooking =
          invoice?.booking && typeof invoice.booking === "object" ? invoice.booking : null;

        if (!resolvedBooking && invoice?.booking && typeof invoice.booking === "string") {
          const bookingSnap = yield call(getDoc, doc(db, BOOKINGS_COLLECTION, invoice.booking));
          if (bookingSnap.exists()) {
            resolvedBooking = { id: bookingSnap.id, ...bookingSnap.data() };
          }
        }

        return {
          ...invoice,
          booking: resolvedBooking,
        };
      })
    );

    // Filter out already batched/refund invoices and enforce booking completed + paid
    const eligibleInvoices = invoicesWithBooking.filter(
      (inv) =>
        !inv.batch_settlement_id &&
        !isRefundMarked(inv) &&
        isBookingCompletedAndPaid(inv.booking)
    );

    if (eligibleInvoices.length === 0) {
      throw new Error("No eligible invoices for batch settlement. Only non-refund invoices with booking_status=completed and paymentStatus=paid are allowed.");
    }

    const VAT_RATE = 0.15;
    
    // Get service provider data
    const providerSnap = yield call(getDoc, doc(db, SERVICE_PROVIDERS_COLLECTION, serviceProviderId));
    if (!providerSnap.exists()) {
      throw new Error("Service provider not found");
    }
    const serviceProvider = { id: providerSnap.id, ...providerSnap.data() };

    // Create batched ERP commission invoice
    const batchResult = yield call(
      createBatchedCommissionERPInvoice,
      eligibleInvoices,
      serviceProvider,
      startDate,
      endDate
    );

    // Update each invoice with batch settlement reference and create individual customer settlements
    yield all(
      eligibleInvoices.map(function* (invoice) {
        const fineDeductions = Number(invoice.fine_deductions_total || 0);
        const grossAmount = Number(
          invoice.total_after_discount_before_fine ||
          invoice.total_after_discount ||
          0
        );
        
        const serviceVAT = grossAmount * (VAT_RATE / (1 + VAT_RATE));
        const serviceNet = grossAmount - serviceVAT;
        
        // Get commission rates
        const appCommissionRate = invoice.app_commission_rate || 0;
        const serviceProviderCommissionRate = invoice.service_provider_commission_rate || 0;
        const paymentMethod = normalize(
          invoice?.booking?.paymentMethod || invoice?.booking?.payment_method || invoice?.payment_method
        );
        const tamaraCommissionRate = paymentMethod === "tamara" ? Number(invoice.tamara_commission || 0) : 0;
        
        // Calculate commissions
        const appCommission = serviceNet * (appCommissionRate / 100);
        const serviceProviderCommission = serviceNet * (serviceProviderCommissionRate / 100);
        const tamaraCommission = serviceNet * (tamaraCommissionRate / 100);
        const totalCommission = appCommission + serviceProviderCommission + tamaraCommission;
        const totalCommissionVAT = totalCommission * VAT_RATE;
        
        // Calculate service provider's actual payout
        const amountAfterCommission = serviceNet - totalCommission;
        const vatOnNetAmount = amountAfterCommission * VAT_RATE;
        const netAmountWithVAT = amountAfterCommission + vatOnNetAmount;
        const finalPayout = Math.max(netAmountWithVAT - fineDeductions, 0);
        
        const netAfterFines = Math.max(grossAmount - fineDeductions, 0);
        const fineMeta = getFineMeta(invoice);

        // Create individual customer settlement for this invoice
        const settlementData = {
          order_id: invoice.booking?.id || null,
          customer_name: invoice.app_user_id?.username || "Unknown User",
          // New commission-aware fields
          original_service_amount: grossAmount,
          platform_commission: totalCommission + totalCommissionVAT,
          app_commission: appCommission,
          service_provider_commission: serviceProviderCommission,
          tamara_commission_rate: tamaraCommissionRate,
          tamara_commission: tamaraCommission,
          app_commission_vat: appCommission * VAT_RATE,
          service_provider_commission_vat: serviceProviderCommission * VAT_RATE,
          tamara_commission_vat: tamaraCommission * VAT_RATE,
          amount_after_commission: amountAfterCommission,
          service_vat: vatOnNetAmount,
          net_amount_with_vat: netAmountWithVAT,
          cancellation_fines: fineDeductions,
          final_payout: finalPayout,
          // Legacy fields
          gross_amount: grossAmount,
          service_vat: serviceVAT,
          service_net: serviceNet,
          net_after_fines: netAfterFines,
          payment_reference: invoice.id,
          date: new Date().toISOString(),
          service_name: invoice.serviceProviderServices?.name || null,
          service_provider_name: invoice.serviceProviders?.salon_name || null,
          fine_reason: invoice.fine_last_reason || fineMeta.defaultReasonAr,
          batch_settlement_id: batchResult.batchSettlementId,
          batch_erp_invoice_id: batchResult.erpData.name,
          period_start: startDate,
          period_end: endDate,
          is_batched: true,
          invoice_count: eligibleInvoices.length,
          deductions:
            fineDeductions > 0
              ? [
                  {
                    type: fineMeta.deductionType,
                    amount: fineDeductions,
                    reason: invoice.fine_last_reason || fineMeta.defaultReasonAr,
                    source:
                      invoice.latest_delay_record_id || invoice.latest_cancellation_id || null,
                  },
                ]
              : [],
        };

        const settlement = yield call(createSettlementStatement, invoice, settlementData);

        // Update invoice with batch reference
        const invoiceRef = doc(db, INVOICES_COLLECTION, invoice.id);
        yield call(updateDoc, invoiceRef, {
          payment_status: 'paid',
          batch_settlement_id: batchResult.batchSettlementId,
          batch_erp_invoice_id: batchResult.erpData.name,
          settlement_id: settlement.settlement_id,
          settled_at: new Date().toISOString(),
        });

      })
    );

    // Calculate total platform revenue and commissions for all invoices
    let totalPlatformRevenue = 0;
    let totalGrossAmount = 0;
    let totalAppCommission = 0;
    let totalServiceProviderCommission = 0;
    let totalTamaraCommission = 0;
    let totalCommission = 0;
    let totalCommissionVAT = 0;
    let totalFines = 0;

    eligibleInvoices.forEach(invoice => {
      const fineDeductions = Number(invoice.fine_deductions_total || 0);
      const grossAmount = Number(
        invoice.total_after_discount_before_fine ||
        invoice.total_after_discount ||
        0
      );
      const serviceVAT = grossAmount * (VAT_RATE / (1 + VAT_RATE));
      const serviceNet = grossAmount - serviceVAT;
      
      const appCommissionRate = Number(invoice.app_commission_rate || 0);
      const serviceProviderCommissionRate = Number(invoice.service_provider_commission_rate || 0);
      const paymentMethod = normalize(
        invoice?.booking?.paymentMethod || invoice?.booking?.payment_method || invoice?.payment_method
      );
      const tamaraCommissionRate = paymentMethod === "tamara" ? Number(invoice.tamara_commission || 0) : 0;
      
      const appCommission = serviceNet * (appCommissionRate / 100);
      const serviceProviderCommission = serviceNet * (serviceProviderCommissionRate / 100);
      const tamaraCommission = serviceNet * (tamaraCommissionRate / 100);
      const invoiceTotalCommission = appCommission + serviceProviderCommission + tamaraCommission;
      const invoiceCommissionVAT = invoiceTotalCommission * VAT_RATE;
      
      totalGrossAmount += grossAmount;
      totalAppCommission += appCommission;
      totalServiceProviderCommission += serviceProviderCommission;
      totalTamaraCommission += tamaraCommission;
      totalCommission += invoiceTotalCommission;
      totalCommissionVAT += invoiceCommissionVAT;
      totalFines += fineDeductions;
      totalPlatformRevenue += (invoiceTotalCommission + invoiceCommissionVAT + fineDeductions);
    });

    // Add one aggregated transaction history entry per batch
    const invoiceIds = eligibleInvoices.map((inv) => inv.id);
    
    // Provider details are already fetched in serviceProvider object
    const providerData = serviceProvider || {};
    
    const hasDelayFinesInBatch = eligibleInvoices.some((invoice) => isDelayFineEntry(invoice));

    const transactionData = {
      service_name: "تسوية مجمعة",
      service_provider_name: serviceProvider?.salon_name || "Unknown Provider",
      service_provider_id: serviceProvider?.id || null,
      service_provider_mobile: serviceProvider?.mobile || "Unknown Mobile",
      service_provider_vat_number: providerData?.tax_number || "Unknown VAT",
      service_provider_commercial_register: providerData?.commercial_register || "Unknown Register",
      service_provider_address: providerData?.address || "Unknown Address",
      user_name: "متعدد العملاء",
      amount_paid: totalPlatformRevenue,
      erp_invoice_id: batchResult.erpData.name,
      batch_settlement_id: batchResult.batchSettlementId,
      is_batched: true,
      invoice_count: eligibleInvoices.length,
      invoice_ids: invoiceIds,
      period_start: startDate,
      period_end: endDate,
      status: "paid",
      created_at: new Date().toISOString(),
      transaction_date: new Date().toISOString(),
      total_platform_revenue: totalPlatformRevenue,
      total_after_discount: totalGrossAmount,
      app_commission: totalAppCommission,
      service_provider_commission: totalServiceProviderCommission,
      tamara_commission: totalTamaraCommission,
      tamara_commission_vat: totalTamaraCommission * VAT_RATE,
      total_commission: totalCommission,
      total_commission_vat: totalCommissionVAT,
      fine_deductions_total: totalFines,
      fine_type: hasDelayFinesInBatch ? "mixed_delay_cancellation" : "cancellation",
      fine_reason: totalFines > 0
        ? hasDelayFinesInBatch
          ? "غرامات تأخير/إلغاء ضمن تسوية مجمعة"
          : "غرامات إلغاء ضمن تسوية مجمعة"
        : null,
    };

    yield call(addDoc, collection(db, "providers_transactions_history"), transactionData);

    // Increment platform revenue
    yield call(
      setDoc,
      doc(db, "platform_revenue", "total"),
      {
        amount: increment(totalPlatformRevenue),
        last_updated: new Date().toISOString(),
      },
      { merge: true }
    );

    // Send notification to service provider
    const notificationData = {
      title: `تم إنشاء تسوية مجمعة`,
      content: `تم إصدار تسوية مجمعة لـ ${eligibleInvoices.length} خدمة من ${startDate} إلى ${endDate}.
      
      إجمالي الإيراد: ${batchResult.erpData.grand_total || 0} ريال
      
      يمكنك مراجعة التفاصيل من لوحة التحكم.`,
      notification_type: "BATCH_SETTLEMENT",
      seen: false,
      serviceProviders: serviceProviderId,
      created_at: new Date().toISOString(),
      batch_settlement_id: batchResult.batchSettlementId,
    };

    yield call(saveNotificationToFirestore, notificationData);

    const notificationTokens = Array.isArray(serviceProvider?.notification_token)
      ? serviceProvider.notification_token.filter(t => t)
      : [];

    yield all(
      notificationTokens.map(token =>
        call(handleNotificationWithRetry, notificationData, token)
      )
    );

    // Refresh transactions
    const transactions = yield call(fetchInvoices);
    yield put({ type: actionTypes.SET_TRANSACTIONS, data: transactions });
    yield put({ 
      type: actionTypes.GENERATE_BATCH_SETTLEMENT_SUCCESS,
      payload: {
        batchSettlementId: batchResult.batchSettlementId,
        erpInvoiceId: batchResult.erpData.name,
        invoiceCount: invoices.length
      }
    });

  } catch (error) {
    console.error("Error generating batch settlement:", error);
    yield put({ 
      type: actionTypes.GENERATE_BATCH_SETTLEMENT_FAILURE, 
      error: error.message 
    });
  }
}


function* handleNotificationWithRetry(notificationData, token) {
  try {
    yield call(createNotificationInAPI, notificationData, token);
  } catch (error) {

    // console.error("Notification failed for token:", token, error);
    // You could add retry logic here if needed
  }
}

async function saveNotificationToFirestore(notificationData) {
  await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData);
}



async function createNotificationInAPI(notificationData, deviceToken) {
  const API_URL = 'https://node-server-eta-coral.vercel.app/send-notification';

  try {
    // Send push notification if token exists
    if (deviceToken) {
      const response = await axios.post(API_URL, {
        deviceToken,
        title: notificationData.title,
        body: notificationData.content
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_NODE_API_KEY
        },
        timeout: 5000 // Add timeout to prevent hanging
      });

      if (response.status !== 200) {
        throw new Error('Notification API returned non-200 status');
      }
    }



  } catch (error) {
    // console.error('Notification creation failed:', error);
    throw error;
  }
}



async function fetchInvoices() {
  try {
    const querySnapshot = await getDocs(collection(db, INVOICES_COLLECTION));
    const invoices = [];

    // Process invoices in parallel for better performance
    await Promise.all(querySnapshot.docs.map(async (docSnap) => {
      const invoiceData = docSnap.data();
      const enrichedInvoice = {
        id: docSnap.id,
        ...invoiceData,
        booking: null,
        serviceProviders: null,
        app_user_id: null,
        serviceProviderServices: null
      };

      // Fetch related data only if references exist
      const fetchPromises = [];

      if (invoiceData.booking) {
        fetchPromises.push(
          getDoc(doc(db, BOOKINGS_COLLECTION, invoiceData.booking))
            .then(async (bookingSnap) => {
              if (bookingSnap.exists()) {
                const bookingData = bookingSnap.data();
                enrichedInvoice.booking = {
                  id: bookingSnap.id,
                  ...bookingData
                };

                // Fetch app user if app_user_id exists in booking
                if (bookingData.app_user_id) {
                  const userSnap = await getDoc(doc(db, APP_USERS_COLLECTION, bookingData.app_user_id));
                  if (userSnap.exists()) {
                    enrichedInvoice.app_user_id = {
                      id: userSnap.id,
                      ...userSnap.data()
                    };
                  }
                }

                // Fetch serviceProviderServices if it exists in booking
                if (bookingData.serviceProviderServices) {
                  const serviceSnap = await getDoc(doc(db, SERVICE_PROVIDER_SERVICES_COLLECTION, bookingData.serviceProviderServices));
                  if (serviceSnap.exists()) {
                    enrichedInvoice.serviceProviderServices = {
                      id: serviceSnap.id,
                      ...serviceSnap.data()
                    };
                  }
                }
              }
            })
        );
      }

      if (invoiceData.serviceProviders) {
        fetchPromises.push(
          getDoc(doc(db, SERVICE_PROVIDERS_COLLECTION, invoiceData.serviceProviders))
            .then(providerSnap => {
              if (providerSnap.exists()) {
                enrichedInvoice.serviceProviders = {
                  id: providerSnap.id,
                  ...providerSnap.data()
                };
              }
            })
        );
      }

      // Wait for all related data fetches to complete
      await Promise.all(fetchPromises);
      invoices.push(enrichedInvoice);
    }));

    return invoices;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
}

function* fetchTransactionsSaga() {
  try {
    const transactions = yield call(fetchInvoices);
    yield put({ type: actionTypes.SET_TRANSACTIONS, data: transactions });
  } catch (error) {
    throw error;
  }
}

export default function* rootSaga() {
  yield takeEvery(actionTypes.FETCH_TRANSACTIONS, fetchTransactionsSaga);
  yield takeEvery(actionTypes.CONFIRM_PAYMENT, confirmPaymentSaga);
  yield takeEvery(actionTypes.GENERATE_BATCH_SETTLEMENT, generateBatchSettlementSaga);
}